import React, { useEffect, useState } from 'react';
import { Bot, Plus, Sparkles, X } from 'lucide-react';
import {
  CLIENT_AUTH_OPTIONS,
  CLIENT_TYPE_OPTIONS,
  MCP_PUBLIC_URL,
  SINGLETON_ENDPOINT_ID,
  clientAuthLabel,
  clientTypeLabel,
  generateRegistrationId,
  isGeneratedRegistrationId,
} from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { createId } from '../governedMcpStore';
import type { ClientAuthOption, ClientType, McpClient } from '../governedMcpTypes';
import { FormSection, FormStack, GatewayDrawer, fieldClass, ghostBtn, labelClass, panelClass, primaryBtn } from '../components/GatewayUi';

function needsAdminKey(type: ClientType): boolean {
  return type === 'claude_enterprise' || type === 'chatgpt_enterprise';
}

function blankClient(occupiedRegistrationIds: string[]): McpClient {
  const clientType: ClientType = 'claude_enterprise';
  return {
    id: createId('cl'),
    name: '',
    clientType,
    registrationId: generateRegistrationId(clientType, occupiedRegistrationIds),
    authConfig: '',
    authOption: '',
    adminKey: '',
    workspaceIds: [],
    customKeyLabel: '',
    customKeyValue: '',
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'development',
    trustPolicy: 'enterprise_managed',
    allowedUserIds: [],
    allowedGroupIds: [],
    status: 'active',
    createdBy: 'Current administrator',
    lastConnectedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function hydrateClient(initial: McpClient): McpClient {
  const authOption: ClientAuthOption =
    initial.authOption === 'oauth' || initial.authOption === 'api_key'
      ? initial.authOption
      : '';
  const workspaceIds = initial.workspaceIds && initial.workspaceIds.length > 0
    ? initial.workspaceIds
    : initial.clientType === 'chatgpt_enterprise'
      ? ['']
      : [];
  return {
    ...initial,
    endpointId: SINGLETON_ENDPOINT_ID,
    authOption,
    adminKey: initial.adminKey ?? '',
    workspaceIds,
    customKeyLabel: initial.customKeyLabel ?? '',
    customKeyValue: initial.customKeyValue ?? '',
  };
}

function buildSavedClient(form: McpClient): McpClient {
  const authOption: ClientAuthOption = form.authOption === 'oauth' || form.authOption === 'api_key' ? form.authOption : '';
  const {
    adminKey: _adminKey,
    workspaceIds: _workspaceIds,
    customKeyLabel: _customKeyLabel,
    customKeyValue: _customKeyValue,
    ...rest
  } = form;
  const next: McpClient = {
    ...rest,
    endpointId: SINGLETON_ENDPOINT_ID,
    environment: 'production',
    authOption,
    authConfig: clientAuthLabel(authOption),
  };
  if (needsAdminKey(form.clientType)) {
    next.adminKey = form.adminKey?.trim() || '';
  }
  if (form.clientType === 'chatgpt_enterprise') {
    next.workspaceIds = (form.workspaceIds || []).map((id) => id.trim()).filter(Boolean);
  }
  if (form.clientType === 'custom') {
    next.customKeyLabel = form.customKeyLabel?.trim() || '';
    next.customKeyValue = form.customKeyValue?.trim() || '';
  }
  return next;
}

export default function ClientFormDrawer({
  open,
  initial,
  occupiedRegistrationIds = [],
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: McpClient | null;
  occupiedRegistrationIds?: string[];
  isLight: boolean;
  onClose: () => void;
  onSave: (client: McpClient) => void;
}) {
  const [form, setForm] = useState<McpClient>(() => blankClient(occupiedRegistrationIds));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? hydrateClient(initial) : blankClient(occupiedRegistrationIds));
    setError(null);
    // Reset only when the drawer opens or the edited client changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const isEditing = Boolean(initial);

  const applyClientType = (nextType: ClientType) => {
    setForm((prev) => {
      const shouldRegenerate = !isEditing && isGeneratedRegistrationId(prev.registrationId);
      const nextId = shouldRegenerate
        ? generateRegistrationId(nextType, [...occupiedRegistrationIds, prev.registrationId])
        : prev.registrationId;
      return {
        ...prev,
        clientType: nextType,
        registrationId: nextId,
        adminKey: needsAdminKey(nextType) ? prev.adminKey : '',
        workspaceIds: nextType === 'chatgpt_enterprise' ? (prev.workspaceIds?.length ? prev.workspaceIds : ['']) : [],
        customKeyLabel: nextType === 'custom' ? prev.customKeyLabel : '',
        customKeyValue: nextType === 'custom' ? prev.customKeyValue : '',
      };
    });
  };

  const generateId = () => {
    if (isEditing) return;
    const next = generateRegistrationId(form.clientType, [...occupiedRegistrationIds, form.registrationId]);
    setForm((prev) => ({ ...prev, registrationId: next }));
    logGovernedMcp('app', 'client_registration_id_generated', { id: form.id, clientType: form.clientType });
  };

  const failValidation = (reason: string, message: string) => {
    logGovernedMcp('warning', 'client_form_validation_failed', { id: form.id, clientType: form.clientType, reason });
    setError(message);
  };

  const submit = () => {
    if (!form.name.trim()) {
      failValidation('name_required', 'Client name is required.');
      return;
    }
    if (!form.registrationId.trim()) {
      failValidation('registration_id_required', 'Registration identifier is required.');
      return;
    }
    if (occupiedRegistrationIds.includes(form.registrationId.trim())) {
      failValidation('registration_id_taken', 'Registration identifier is already in use.');
      return;
    }
    if (form.clientType === 'custom') {
      const label = form.customKeyLabel?.trim() || '';
      const value = form.customKeyValue?.trim() || '';
      if (Boolean(label) !== Boolean(value)) {
        failValidation('custom_key_pair', 'Provide both a key label and value, or leave both empty.');
        return;
      }
    }
    onSave(buildSavedClient(form));
  };

  const workspaceIds = form.workspaceIds && form.workspaceIds.length > 0 ? form.workspaceIds : [''];
  const authLabel = clientAuthLabel(form.authOption);

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Clients"
      icon={<Bot size={18} className="text-indigo-500" />}
      title={initial ? form.name || 'Edit MCP client' : 'Register AI client'}
      subtitle="The client requests. Selfic maps identity and decides what it may discover or invoke."
      status={{ label: form.status === 'active' ? 'Active' : form.status, tone: form.status === 'active' ? 'success' : 'neutral' }}
      isLight={isLight}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn()} onClick={submit}>{initial ? 'Save client' : 'Register client'}</button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <div className="space-y-5">
          <FormSection title="Identity" description="How this AI application is registered in Selfic." isLight={isLight}>
            <div className="space-y-4">
              <div>
                <label className={labelClass(isLight)}>Client name</label>
                <input className={fieldClass(isLight)} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Claude Enterprise — Risk Desk" />
              </div>
              <div>
                <label className={labelClass(isLight)}>Client type</label>
                <select className={fieldClass(isLight)} value={form.clientType} onChange={(e) => applyClientType(e.target.value as ClientType)}>
                  {CLIENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  {CLIENT_TYPE_OPTIONS.find((option) => option.value === form.clientType)?.hint}
                </p>
              </div>
              <div>
                <label className={labelClass(isLight)}>Registration identifier</label>
                <div className="flex gap-2">
                  <input
                    className={`${fieldClass(isLight)} font-mono`}
                    value={form.registrationId}
                    onChange={(e) => setForm((p) => ({ ...p, registrationId: e.target.value }))}
                    placeholder="claude_482913"
                  />
                  <button type="button" className={`${ghostBtn(isLight)} shrink-0`} onClick={generateId} disabled={isEditing}>
                    <Sparkles size={14} /> Generate
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass(isLight)}>Authentication</label>
                <select
                  className={fieldClass(isLight)}
                  value={form.authOption || ''}
                  onChange={(e) => setForm((p) => ({ ...p, authOption: e.target.value as ClientAuthOption }))}
                >
                  {CLIENT_AUTH_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                  Optional. Choose how this client authenticates to the gateway.
                </p>
              </div>
              {needsAdminKey(form.clientType) ? (
                <div>
                  <label className={labelClass(isLight)}>Admin key (optional)</label>
                  <input
                    type="password"
                    autoComplete="off"
                    className={fieldClass(isLight)}
                    value={form.adminKey || ''}
                    onChange={(e) => setForm((p) => ({ ...p, adminKey: e.target.value }))}
                    placeholder="Enter the organization admin key"
                  />
                </div>
              ) : null}
              {form.clientType === 'chatgpt_enterprise' ? (
                <div>
                  <label className={labelClass(isLight)}>Workspace ids (optional)</label>
                  <div className="space-y-2">
                    {workspaceIds.map((workspaceId, index) => (
                      <div key={`workspace-${index}`} className="flex gap-2">
                        <input
                          className={`${fieldClass(isLight)} font-mono`}
                          value={workspaceId}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => {
                              const current = p.workspaceIds && p.workspaceIds.length > 0 ? [...p.workspaceIds] : [''];
                              current[index] = value;
                              return { ...p, workspaceIds: current };
                            });
                          }}
                          placeholder="ws-…"
                        />
                        {workspaceIds.length > 1 ? (
                          <button
                            type="button"
                            className={`${ghostBtn(isLight)} shrink-0 px-2`}
                            onClick={() => {
                              setForm((p) => {
                                const current = (p.workspaceIds || []).filter((_, itemIndex) => itemIndex !== index);
                                return { ...p, workspaceIds: current.length > 0 ? current : [''] };
                              });
                            }}
                            aria-label="Remove workspace id"
                          >
                            <X size={14} />
                          </button>
                        ) : null}
                      </div>
                    ))}
                    <button
                      type="button"
                      className={ghostBtn(isLight)}
                      onClick={() => setForm((p) => ({ ...p, workspaceIds: [...(p.workspaceIds && p.workspaceIds.length ? p.workspaceIds : ['']), ''] }))}
                    >
                      <Plus size={14} /> Add workspace
                    </button>
                  </div>
                </div>
              ) : null}
              {form.clientType === 'custom' ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass(isLight)}>Key label (optional)</label>
                    <input
                      className={fieldClass(isLight)}
                      value={form.customKeyLabel || ''}
                      onChange={(e) => setForm((p) => ({ ...p, customKeyLabel: e.target.value }))}
                      placeholder="Authorization"
                    />
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Key value (optional)</label>
                    <input
                      type="password"
                      autoComplete="off"
                      className={fieldClass(isLight)}
                      value={form.customKeyValue || ''}
                      onChange={(e) => setForm((p) => ({ ...p, customKeyValue: e.target.value }))}
                      placeholder="Secret value"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </FormSection>
        </div>

        <div className="space-y-4">
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Registration</h3>
            <dl className={`mt-3 space-y-3 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              <div>
                <dt>Type</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{clientTypeLabel(form.clientType)}</dd>
              </div>
              <div>
                <dt>Identifier</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{form.registrationId || '—'}</dd>
              </div>
              <div>
                <dt>MCP URL</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
              </div>
              <div>
                <dt>Auth</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{authLabel || 'Not specified'}</dd>
              </div>
            </dl>
          </section>
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Identity rule</h3>
            <p className={`text-sm mt-2 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Client-asserted user ids are ignored. The IdP subject is mapped to the enterprise user, then existing Selfic permissions apply.
            </p>
          </section>
        </div>
      </FormStack>
    </GatewayDrawer>
  );
}
